import LogMonitor from "@redux-devtools/log-monitor";
import React, { Children } from "react";
import {
	liftReducerWith as wrapReducerWithDevToolsProfiler,
	unliftStore as unwrapDevToolsStore,
	unliftState as unwrapDevToolsState,
} from "@redux-devtools/instrument";
import { Reducer } from "@reduxjs/toolkit";
import { StoreEnhancer } from "@reduxjs/toolkit";

export const logMonitor = <LogMonitor theme="solarized" />;

const monitorReducer = (state, action) => Children.only(logMonitor).type.update(logMonitor.props, state, action);

// true if the requested action is in the list of quashed actions
const actionIsQuashed = (actionType?: string, root?: any) => {
	if (!actionType || !root) return false;
	return root.quashedActions?.includes(actionType) ?? false;
};

/**
 * For performance reasons, we dynamically bypass DevTools functionality whenever it's not in use.
 *
 * A DevTools is in use if the service's window is visible or the `visibility` value is true in the service's config
 *
 * In order to bypass DevTools, we have to create our own store enhancer that will initialize DevTools and then
 * dynamically choose which reducer to dispatch actions to based on what state we're in.
 *
 */
export default (function (doCreateStore) {
	return (currentReducer, preloadedState) => {
		const wrappedReducer = wrapReducerWithDevToolsProfiler(
			currentReducer,
			preloadedState,
			monitorReducer,
			{}
		) as Reducer;

		let didInit = false;

		const reducer = (wrappedState, wrappedAction) => {
			if (!didInit) {
				didInit = true;
				return wrappedReducer(wrappedState, wrappedAction);
			}

			const unwrappedAction = wrappedAction.action;
			const unwrappedState = unwrapDevToolsState(wrappedState);
			const isWindowVisible = unwrappedState && unwrappedState.root.isVisible;
			const isDevToolsAction = wrappedAction.type !== "PERFORM_ACTION" || !unwrappedAction;
			const isBecomingVisible =
				unwrappedAction && unwrappedAction.type === "root/windowVisible" && unwrappedAction.payload === true;

			const devToolsNotVisible = !isDevToolsAction && !isWindowVisible && !isBecomingVisible;
			const bypassDevTools = devToolsNotVisible || actionIsQuashed(unwrappedAction?.type, unwrappedState?.root);

			if (bypassDevTools) {
				wrappedState.computedStates[wrappedState.currentStateIndex].state = currentReducer(
					unwrappedState,
					unwrappedAction
				);

				return wrappedState;
			}

			return wrappedReducer(wrappedState, wrappedAction);
		};

		const wrappedStore = doCreateStore(reducer);

		return unwrapDevToolsStore(wrappedStore, reducer as any, {});
	};
} as StoreEnhancer);
